import { useEffect, useMemo, useState } from "react";
import { updateOnboardingAll, updateOnboardWorking } from "../utils/api.js";
import { loadActiveSiteNames, peekActiveSiteNames } from "../utils/sitesCache.js";
import CustomSelect from "./shared/CustomSelect";
import {
  FIELD_LABELS,
  DATE_FIELDS,
  EDITABLE_FIELDS,
  NEW_COMPLETED_FIELDS,
  NEW_COMPLETED_OPTIONS,
  POSITION_OPTIONS,
  WORKING_OPTIONS,
  NO_MASTER_LINK_CATEGORY,
  EMPLOYMENT_VISIBILITY,
  CASUAL_EMPLOYEE_VISIBILITY,
  TOP_FIELD_ORDER,
  SUBMIT_KEYS,
  WEEKDAYS,
  formatMobile,
  parseWorkingDays,
  formatWorkingDays,
  typeOptionsForStatus,
  validateSubmission,
} from "../utils/onboardingFields.js";

/* ============================================================================
 * OnboardingEditForm — shared modal editor
 * ----------------------------------------------------------------------------
 * Renders the onboarding record editor inside a modal. Used by
 * OnboardingAllUsers (record button) and OnboardActive (row click).
 *
 * Props:
 *   record       – the record object to edit (must include recordId + fields;
 *                  OnboardActive also passes `category` + `masterLink`)
 *   userRecords  – the same person's sibling records (for the conversion guard)
 *   onClose()    – close the modal without saving
 *   onSaved(payload) – called after a successful save with the merged payload;
 *                      the host merges it into its local records + shows a toast.
 *                      Also called with { recordId, working } when the
 *                      Working field saves on its own (modal stays open).
 *
 * Site Working is a CustomSelect fed by the page-level sitesCache (/api/sites).
 * Active sites are offered; the record's current site is always kept in the
 * list even if it has since gone Non Active, so the value never disappears.
 *
 * Working Days (WorkingDays in Airtable) is a row of click-on/click-off day
 * buttons. The current value ("Mon,Tues,…" or "None") lights the matching
 * days; the field is held as a Mon→Sun ordered string the whole time. At
 * least one day must be lit — Submit is disabled and the guard rejects "None".
 *
 * Working (Working / Not Working) lives on the MAIN table, not the onboarding
 * table. It saves the moment it's changed, through its own endpoint
 * (updateOnboardWorking → PATCH /api/onboard-active/:id/working), so Submit
 * never touches it. It is disabled on NU No Form rows — no Master-Link yet,
 * so there is no main-table record to update.
 * ==========================================================================*/

const GRID_3COL = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  columnGap: "20px",
  rowGap: "12px",
};

const dayBtnStyle = (on) => ({
  padding: "6px 11px",
  borderRadius: "8px",
  fontSize: "0.82rem",
  fontWeight: on ? 600 : 500,
  cursor: "pointer",
  border: on ? "1px solid #22d3ee" : "1px solid rgba(148,163,184,0.4)",
  background: on ? "#22d3ee" : "transparent",
  color: on ? "#0f172a" : "#cbd5e1",
  transition: "all 0.15s ease",
});


const OnboardingEditForm = ({ record, userRecords = [], onClose, onSaved }) => {
  // Normalise WorkingDays on open so "mon, tues" / "" / "None" all become the
  // canonical Mon→Sun string (or "None") before any edit happens.
  const [formData, setFormData] = useState(() => ({
    ...record,
    workingDays: formatWorkingDays(parseWorkingDays(record?.workingDays)),
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Working — saves independently of Submit.
  const [workingSaving, setWorkingSaving] = useState(false);
  const [workingError, setWorkingError] = useState(null);

  // Site list for the Site Working select — seeded synchronously from the
  // page-level cache so the select renders fully populated on first paint.
  const [activeSites, setActiveSites] = useState(() => peekActiveSiteNames() ?? []);

  useEffect(() => {
    let alive = true;
    loadActiveSiteNames().then((names) => { if (alive) setActiveSites(names); });
    return () => { alive = false; };
  }, []);

  /* active sites, A→Z, plus the current value if it isn't in the list */
  const siteOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    activeSites.forEach((n) => {
      const k = n.toLowerCase();
      if (!seen.has(k)) { seen.add(k); out.push(n); }
    });
    out.sort((a, b) => a.localeCompare(b));
    const current = (formData.siteWorking ?? "").toString().trim();
    if (current && !seen.has(current.toLowerCase())) out.unshift(current);
    return out;
  }, [activeSites, formData.siteWorking]);

  // Type Of Employment options are locked to the record's Work Status.
  const typeOptions = typeOptionsForStatus(formData.workStatus);

  // Which employment-driven fields to reveal (Casual employees get a reduced set).
  const employmentBlockKeys =
    formData.typeOfEmployment === "Employee" && formData.position === "Casual"
      ? CASUAL_EMPLOYEE_VISIBILITY
      : EMPLOYMENT_VISIBILITY[formData.typeOfEmployment] ?? [];

  // Working Days — lit days, and whether the record is blocked from saving.
  const selectedDays = parseWorkingDays(formData.workingDays);
  const noWorkingDays = selectedDays.length === 0;

  // Working is locked when there's no main-table record behind this row.
  const workingLocked =
    formData.category === NO_MASTER_LINK_CATEGORY ||
    !(formData.masterLink ?? "").toString().trim();

  const handleFieldChange = (key, value) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const nextVal = key === "mobile" ? formatMobile(value) : value;
      return { ...prev, [key]: nextVal };
    });
  };

  const toggleDay = (day) => {
    const next = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    handleFieldChange("workingDays", formatWorkingDays(next));
    if (next.length) setError(null);
  };

  /* Working — optimistic update, own endpoint, rolls back on failure.
   * Reports to the host through onSaved so its table row + category update,
   * but does NOT close the modal. */
  const handleWorkingChange = async (next) => {
    if (workingLocked || workingSaving || submitting) return;
    const prevVal = formData.working ?? "";
    if (next === prevVal) return;

    setWorkingSaving(true);
    setWorkingError(null);
    handleFieldChange("working", next);
    try {
      const res = await updateOnboardWorking(formData.recordId, next);
      if (res?.status !== "success") {
        throw new Error(res?.message || res?.error || "Failed to update Working");
      }
      onSaved?.({ recordId: formData.recordId, working: next });
    } catch (err) {
      handleFieldChange("working", prevVal);
      setWorkingError(err.message || "Failed to update Working");
    } finally {
      setWorkingSaving(false);
    }
  };

  const renderField = (key) => {
    const label = FIELD_LABELS[key];
    const isDate = DATE_FIELDS.has(key);
    const value = formData[key] ?? "";

    // Work Status is read-only — it's determined by the entity type and can
    // never legally flip, so it isn't editable here.
    if (key === "workStatus") {
      return (
        <div className="ob-field" key={key}>
          <label className="ob-label">{label}</label>
          <input type="text" value={value} readOnly className="ob-input ob-input-readonly" />
        </div>
      );
    }

    // Working Days — click-on / click-off day buttons, spans the two columns
    // next to TFN Number.
    if (key === "workingDays") {
      return (
        <div className="ob-field" key={key} style={{ gridColumn: "span 2" }}>
          <label className="ob-label">{label}</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "4px" }}>
            {WEEKDAYS.map((day) => {
              const on = selectedDays.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleDay(day)}
                  aria-pressed={on}
                  disabled={submitting}
                  style={dayBtnStyle(on)}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {noWorkingDays && (
            <div className="ob-helper" style={{ color: "#f87171", marginTop: "6px" }}>
              Select at least one working day.
            </div>
          )}
        </div>
      );
    }

    // Working — main-table field, saves on change via its own endpoint.
    if (key === "working") {
      const busy = workingLocked || workingSaving || submitting;
      let hint;
      let hintColor = "rgba(255,255,255,0.45)";
      if (workingError) { hint = workingError; hintColor = "#f87171"; }
      else if (workingLocked) hint = "Not available until this record has a Master-Link (NU No Form).";
      else if (workingSaving) hint = "Saving…";
      else hint = "Saves straight away — not part of Submit.";
      return (
        <div className="ob-field" key={key}>
          <div style={busy ? { opacity: 0.55, pointerEvents: "none" } : undefined}>
            <CustomSelect
              label={label}
              value={value}
              onChange={handleWorkingChange}
              options={WORKING_OPTIONS}
              disabled={busy}
            />
          </div>
          <div className="ob-helper" style={{ color: hintColor, marginTop: "4px", fontSize: "12px" }}>
            {hint}
          </div>
        </div>
      );
    }

    if (EDITABLE_FIELDS.has(key)) {
      if (key === "typeOfEmployment") {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={typeOptions}
            />
          </div>
        );
      }
      if (key === "position") {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={POSITION_OPTIONS}
            />
          </div>
        );
      }
      if (key === "siteWorking") {
        // Always a CustomSelect — before the list arrives it holds just the
        // current value, so the field never changes shape.
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={siteOptions}
            />
          </div>
        );
      }
      if (NEW_COMPLETED_FIELDS.has(key)) {
        return (
          <div className="ob-field" key={key}>
            <CustomSelect
              label={label}
              value={value}
              onChange={(v) => handleFieldChange(key, v)}
              options={NEW_COMPLETED_OPTIONS}
            />
          </div>
        );
      }
      return (
        <div className="ob-field" key={key}>
          <label className="ob-label">{label}</label>
          <input
            type={isDate ? "date" : "text"}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
            className="ob-input"
            placeholder=""
          />
        </div>
      );
    }

    // Read-only
    return (
      <div className="ob-field" key={key}>
        <label className="ob-label">{label}</label>
        <input
          type={isDate ? "date" : "text"}
          value={value}
          readOnly
          className="ob-input ob-input-readonly"
          placeholder=""
        />
      </div>
    );
  };

  const buildSubmitPayload = () => {
    const payload = { recordId: formData.recordId };
    SUBMIT_KEYS.forEach((key) => {
      payload[key] = formData[key] ?? "";
    });
    // Always send the canonical Mon→Sun string.
    payload.workingDays = formatWorkingDays(parseWorkingDays(formData.workingDays));
    return payload;
  };

  const handleSubmit = async () => {
    if (submitting || workingSaving) return;
    const payload = buildSubmitPayload();

    // Safety net — the UI can't offer an illegal flip or an empty Working
    // Days, but guard anyway.
    const validation = validateSubmission(payload, userRecords);
    if (!validation.ok) {
      setError(validation.message);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await updateOnboardingAll(payload);
      if (res?.status === "success") {
        onSaved?.(payload);
        onClose?.();
      } else {
        throw new Error(res?.message || "Failed to update record");
      }
    } catch (err) {
      setError(err.message || "Failed to update record");
    } finally {
      setSubmitting(false);
    }
  };

  const closeDisabled = submitting || workingSaving;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeDisabled ? undefined : onClose}
        style={{ position: "fixed", inset: 0, top: "var(--fr-modal-top, 0px)", background: "rgba(0,0,0,0.7)", zIndex: 998 }}
      />
      {/* Modal */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          top: "calc(var(--fr-modal-top, 0px) + (100dvh - var(--fr-modal-top, 0px) - 68px) / 2)",   // centred UNDER the info bar
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(1100px, 94vw)",
          maxHeight: "min(90vh, calc(100dvh - var(--fr-modal-top, 0px) - 108px))",
          overflowY: "auto",
          zIndex: 999,
        }}
      >
        <div
          className="ob-form-card"
          style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", maxHeight: "none" }}
        >
          <div className="ob-form-fields">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <h2 className="ob-helper" style={{ fontSize: "20px", margin: 0, fontWeight: 600 }}>
                {formData.workStatus} Record
              </h2>
              <button
                onClick={onClose}
                disabled={closeDisabled}
                style={{
                  background: "none",
                  border: "none",
                  cursor: closeDisabled ? "not-allowed" : "pointer",
                  color: "#6b7280",
                  fontSize: "14px",
                }}
              >
                Close
              </button>
            </div>

            {error && (
              <div className="ob-info-banner ob-info-banner-warning" style={{ marginBottom: "12px" }}>
                {error}
              </div>
            )}

            {/* Top, always-visible block */}
            <div style={GRID_3COL}>{TOP_FIELD_ORDER.map(renderField)}</div>

            <div className="ob-divider" />

            {/* Employment-driven block */}
            <div style={{ color: "#22d3ee", fontSize: "13px", fontWeight: 500, marginBottom: "8px" }}>
              Section below adjusts based on Type Of Employment.
            </div>

            {employmentBlockKeys.length === 0 ? (
              <div style={{ color: "#6b7280", fontSize: "13px", padding: "8px 0" }}>
                Select a Type Of Employment to reveal the relevant fields.
              </div>
            ) : (
              <div style={GRID_3COL}>{employmentBlockKeys.map(renderField)}</div>
            )}

            <div className="ob-divider" />

            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
              <button
                onClick={onClose}
                disabled={closeDisabled}
                className="ob-btn ob-btn-check"
                style={{ minWidth: "120px" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || workingSaving || noWorkingDays}
                title={noWorkingDays ? "Select at least one working day first" : undefined}
                className="ob-btn ob-btn-submit"
                style={{ minWidth: "120px", opacity: noWorkingDays ? 0.6 : 1 }}
              >
                {submitting ? "Submitting…" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingEditForm;